import type {
  AssistedAffiliationDraft,
  AssistedAffiliationDraftPayload,
} from "@/services/assistedAffiliation/types";
import { STATUS_LABELS, STATUS_BY_LABEL, initialAssistedAffiliationForm } from "./constants";
import type { AssistedAffiliationForm, AssistedAffiliationStatus } from "./types";

// El servicio guarda el tramo de ventas como código y el formulario como la frase que se ve en
// pantalla. Un código que no esté aquí deja el campo vacío en vez de inventar un tramo.
const MONTHLY_SALES_BY_CODE: Record<string, string> = {
  "10K_500K": "RD$10,000 - RD$500,000",
  "500K_5M": "RD$500,000 - RD$5,000,000",
  "5M_15M": "RD$5,000,000 - RD$15,000,000",
};

const ACCOUNT_TYPE_BY_CODE: Record<string, string> = {
  AHORRO: "Ahorros",
  CORRIENTE: "Corriente",
};

// Solo se encienden los medios de cobro cuyo producto está confirmado: Billet Smart vive en
// `smartpos-device` y BilleTap es su propio tipo. Para los demás no hay correspondencia
// documentada, y encender un toggle por parecido mostraría un producto que nadie solicitó.
const CONFIRMED_PRODUCT_TOGGLES: Record<string, "billetSmart" | "billetap"> = {
  SMARTPOS: "billetSmart",
  BILLET_TAP: "billetap",
};

/**
 * Traduce la solicitud que devuelve el servicio al modelo que edita el formulario.
 * Es una traducción CON PÉRDIDA: hay campos del formulario que el servicio no guarda, y esos
 * quedan en su valor inicial.
 */
export const draftToForm = (
  draft: AssistedAffiliationDraft
): AssistedAffiliationForm => {
  const activeToggle = CONFIRMED_PRODUCT_TOGGLES[draft.product?.product_type];

  return {
    ...initialAssistedAffiliationForm,
    documentNumber: draft.document_number ?? "",
    legalName: draft.legal_name ?? "",
    commercialName: draft.commercial_name ?? "",
    monthlySales: MONTHLY_SALES_BY_CODE[draft.average_sales] ?? "",
    // El select de tipo de negocio guarda el MCC; `detail_category` es solo su etiqueta.
    businessType: draft.commercial_category ?? "",
    representativeNames: draft.representative_first_name ?? "",
    representativeLastNames: draft.representative_last_name ?? "",
    representativeEmail: draft.representative_email ?? "",
    representativePhone: draft.representative_phone ?? "",
    isForeignRepresentative: Boolean(draft.representative_is_foreign),
    passportNumber: draft.representative_passport_number ?? "",
    nationality: draft.representative_nationality ?? "",
    // Los selects de geografía y banco guardan identificadores, no nombres.
    province: draft.province_id ?? "",
    municipality: draft.municipality_id ?? "",
    sector: draft.sector_id ?? "",
    neighborhood: draft.neighborhood_id ?? "",
    streetAndNumber: draft.street_and_number ?? "",
    reference: draft.reference ?? "",
    bank: draft.bank_id ?? "",
    accountNumber: draft.account_number ?? "",
    accountType: ACCOUNT_TYPE_BY_CODE[draft.bank_account] ?? "",
    internalComments: draft.description ?? "",
    creationStatus:
      STATUS_LABELS[draft.status as AssistedAffiliationStatus] ?? "",
    billetSmart: activeToggle === "billetSmart",
    terminalBillet: false,
    paymentLink: false,
    billetap: activeToggle === "billetap",
  };
};

// Las tablas inversas se derivan de las de arriba en vez de escribirse a mano: dos listas paralelas
// se desincronizan en cuanto alguien agrega un tramo o un tipo de cuenta.
const invert = (table: Record<string, string>): Record<string, string> =>
  Object.fromEntries(
    Object.entries(table).map(([code, label]) => [label, code])
  );

const MONTHLY_SALES_BY_LABEL = invert(MONTHLY_SALES_BY_CODE);
const ACCOUNT_TYPE_BY_LABEL = invert(ACCOUNT_TYPE_BY_CODE);

/**
 * Traduce una etiqueta de pantalla al código que espera el servicio.
 * Un campo vacío significa que el usuario lo borró, y eso se envía. Solo cuando la etiqueta existe
 * pero no está en la tabla se conserva el valor previo, porque ahí el hueco es de la tabla y no una
 * decisión de quien edita.
 */
const toCode = (
  table: Record<string, string>,
  label: string,
  previous: string
): string => {
  if (!label) return "";
  return table[label] ?? previous;
};

/**
 * Arma el draft que se manda al guardar.
 * PARTE DE LA SOLICITUD ORIGINAL a propósito y solo sobreescribe lo que el formulario controla: el
 * servicio guarda más cosas de las que la pantalla muestra (los indicadores PEP, `mcc_itbis`, las
 * validaciones), y reconstruir el envío desde el formulario las borraría sin que se note.
 */
export const formToDraft = (
  original: AssistedAffiliationDraft,
  form: AssistedAffiliationForm
): AssistedAffiliationDraft => ({
  ...original,
  // Identifica la solicitud y no se edita. Se toma del original y no del formulario para que un
  // valor alterado en pantalla no pueda mandar el guardado a otro registro.
  document_number: original.document_number,
  legal_name: form.legalName,
  commercial_name: form.commercialName.trim(),
  description: form.internalComments.trim() || null,
  average_sales: toCode(MONTHLY_SALES_BY_LABEL, form.monthlySales, original.average_sales),
  commercial_category: form.businessType,
  representative_first_name: form.representativeNames,
  representative_last_name: form.representativeLastNames,
  representative_email: form.representativeEmail,
  representative_phone: form.representativePhone,
  representative_is_foreign: form.isForeignRepresentative,
  representative_passport_number: form.passportNumber || null,
  representative_nationality: form.nationality,
  province_id: form.province,
  municipality_id: form.municipality,
  sector_id: form.sector,
  neighborhood_id: form.neighborhood,
  street_and_number: form.streetAndNumber,
  reference: form.reference,
  bank_id: form.bank,
  account_number: form.accountNumber,
  bank_account: toCode(ACCOUNT_TYPE_BY_LABEL, form.accountType, original.bank_account),
  status: toCode(STATUS_BY_LABEL, form.creationStatus, original.status),
});

/**
 * Nombres que el servicio de creación pide además de los identificadores. El formulario guarda ids,
 * así que la pantalla los resuelve con sus catálogos y los pasa ya traducidos.
 */
export type DraftPayloadLabels = {
  provinceName: string;
  municipalityName: string;
  sectorName: string;
  neighborhoodName: string;
  detailCategory: string;
};

const PRODUCT_TYPE_BY_TOGGLE: [keyof AssistedAffiliationForm, string][] = [
  ["billetSmart", "SMARTPOS"],
  ["billetap", "BILLET_TAP"],
];

/**
 * Arma el payload de creación desde el formulario.
 * A diferencia del guardado, aquí no hay registro previo del cual partir: lo que el formulario no
 * captura viaja vacío en vez de inventado. Los campos sin origen están listados en
 * viaja vacío en vez de inventado.
 */
export const formToDraftPayload = (
  form: AssistedAffiliationForm,
  labels: DraftPayloadLabels
): AssistedAffiliationDraftPayload => {
  const activeProduct = PRODUCT_TYPE_BY_TOGGLE.find(([toggle]) => form[toggle]);

  return {
    document_number: form.documentNumber.trim(),
    document_type: "RNC",
    legal_name: form.legalName,
    commercial_name: form.commercialName.trim(),
    province_id: form.province,
    province_name: labels.provinceName,
    municipality_id: form.municipality,
    municipality_name: labels.municipalityName,
    sector_id: form.sector,
    sector_name: labels.sectorName,
    neighborhood_id: form.neighborhood,
    neighborhood_name: labels.neighborhoodName,
    street_and_number: form.streetAndNumber,
    reference: form.reference,
    commercial_category: form.businessType,
    detail_category: labels.detailCategory,
    // El formulario no distingue formal de informal, así que no se manda un valor inventado.
    business_type: "",
    average_sales: MONTHLY_SALES_BY_LABEL[form.monthlySales] ?? "",
    // Los documentos se resuelven cuando exista la subida con presigned URL: hasta entonces no hay
    // ninguna ruta real que mandar.
    rnc_certification: "",
    commercial_register: "",
    representative_act: "",
    bylaws: "",
    attachment: "",
    // El formulario no captura los indicadores PEP ni el de ITBIS.
    pep_company: false,
    pep_representative: false,
    rep_representative: false,
    mcc_itbis: false,
    account_number: form.accountNumber,
    bank_account: ACCOUNT_TYPE_BY_LABEL[form.accountType] ?? "",
    bank_id: form.bank,
    representative_phone: form.representativePhone,
    representative_email: form.representativeEmail,
    representative_first_name: form.representativeNames,
    representative_last_name: form.representativeLastNames,
    representative_is_foreign: form.isForeignRepresentative,
    representative_nationality: form.nationality,
    representative_country_code: form.nationality,
    // El payload admite un solo producto y el formulario ofrece cuatro medios de cobro: se manda el
    // primero cuya correspondencia está confirmada.
    product: {
      product_type: activeProduct ? activeProduct[1] : "",
      amount: 1,
      price: 0,
      status: "ACTIVE",
    },
  };
};
